/* eslint-disable no-use-before-define */
import React, { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Input,
  FormControl,
  FormLabel,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useToast,
  Select,
  Flex,
  Checkbox
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as eva from 'eva-icons'
import { log } from '../../utils/logger'
import { createCategoriesActivitiesApi } from './../../utils/api-interface'
import { Activity, Category, CategoryListResponse, CreateActivityRequest, CreateActivityRequestPossibleRolesInner, LocalizedName } from '../../services/network'
import axios from 'axios'
import { languages, IActivityName, ICollapsedLangs, IRoleName } from '../../utils/constants'
import {
  handleRoleNameChange,
  handlePossibleRolesChange,
  updatePossibleRoleName,
  handleCurrentRoleChange,
  handleActivityNameChange
} from '../../utils/modalActivitiesCategoriesUtils'

interface ICreateActivityModalProps {
  isOpen: boolean
  onClose: () => void
  categories: Category[]
  setCategories: React.Dispatch<React.SetStateAction<CategoryListResponse | undefined>>
}

function CreateActivityModal({ isOpen, onClose, categories, setCategories }: ICreateActivityModalProps): JSX.Element {
  const { t } = useTranslation()
  const [showTranslateMenu, setShowTranslateMenu] = useState(false)
  const [confirmButtonDisabled, setConfirmButtonDisabled] = useState(true)
  const [createActivityRequest, setCreateActivityRequest] = useState<CreateActivityRequest>()
  const toast = useToast()
  const [open, setOpen] = useState(isOpen)
  const [categoyRef, setCategoyRef] = useState('')
  const [roleNames, setRoleNames] = useState<IRoleName>(languages.reduce((acc, lang) => ({ ...acc, [lang.code]: '' }), {}))
  const [showNewRoleEdit, setShowNewRoleEdit] = useState(false)
  const [possibleRoles, setPossibleRoles] = useState<CreateActivityRequestPossibleRolesInner[]>([])
  const [confirmTranslateButtonDisabled, setConfirmTranslateButtonDisabled] = useState(false)
  const [focusedInput, setFocusedInput] = useState<string | null>(null)
  const [editingRoleIndex, setEditingRoleIndex] = useState(-1)
  const [currentRoleValue, setCurrentRoleValue] = useState<IRoleName>(languages.reduce((acc, lang) => ({ ...acc, [lang.code]: '' }), {}))
  const [collapsedLangs, setCollapsedLangs] = useState<ICollapsedLangs>(languages.reduce((acc, lang) => ({ ...acc, [lang.code]: false }), {}))
  const [activityNames, setActivityNames] = useState<IActivityName>(languages.reduce((acc, lang) => ({ ...acc, [lang.code]: '' }), {}))
  const [requiredExperience, setRequiredExperience] = useState(false)

  const handleCategoryRefChange = (e: { target: { value: React.SetStateAction<string> } }): void => {
    setCategoyRef(e.target.value)
  }

  const handleRoleNameChangeCallback = useCallback(
    (langCode: string, value: string) => {
      handleRoleNameChange(langCode, value, roleNames, setRoleNames, setCurrentRoleValue, showTranslateMenu)
    },
    [roleNames, showTranslateMenu]
  )

  const handleCurrentRoleChangeCallback = useCallback(
    (langCode: string, value: string) => {
      handleCurrentRoleChange(langCode, value, showTranslateMenu, currentRoleValue, setCurrentRoleValue)
    },
    [currentRoleValue, showTranslateMenu]
  )

  const handleRequiredExperience = (): void => {
    setRequiredExperience((prev) => !prev)
  }

  const handleEditClick = (index: number, roleName: LocalizedName): void => {
    setEditingRoleIndex(index)
    setCurrentRoleValue({
      ...roleName
    })
  }

  const handleCancelClick = (): void => {
    setPossibleRoles((prevRoles) => prevRoles.filter((role) => role.name.it !== currentRoleValue.it))
    setEditingRoleIndex(-1)
  }

  const handleSaveActivityClick = (): void => {
    const isDuplicate = possibleRoles.some((role, index) => index !== editingRoleIndex && role.name.it === currentRoleValue.it)
    if (!isDuplicate) {
      setPossibleRoles((prevRoles) =>
        prevRoles.map((role, index) => (index === editingRoleIndex ? { ...role, name: currentRoleValue as unknown as LocalizedName } : role))
      )
      setEditingRoleIndex(-1)
    } else {
      toast({
        title: t('duplicate_activities'),
        status: 'error',
        duration: 2000,
        isClosable: true
      })
    }
  }

  const addActivityToCategory = (newActivity: Activity, categoryId: string): void => {
    setCategories((prevCategories) => {
      if (!prevCategories) return prevCategories // Handle the case if categories are not yet set

      const updatedCategories = prevCategories.categories.map((category) => {
        if (category.id === categoryId) {
          // If activities is undefined, initialize it with an empty array
          const updatedActivities = category.activities ? [...category.activities, newActivity] : [newActivity]
          // Return the updated category with new activities
          return {
            ...category,
            activities: updatedActivities
          }
        }
        return category // Return the category unchanged if no match
      })

      // Return the new state with updated categories
      return { categories: updatedCategories }
    })
  }

  const postNewActivity = async (): Promise<void> => {
    if (createActivityRequest) {
      try {
        setConfirmButtonDisabled(true)
        const categoriesActivitiesApi = createCategoriesActivitiesApi()
        const response = await categoriesActivitiesApi.createActivity(createActivityRequest)
        toast({
          title: t('activity_created_toast'),
          status: 'success',
          duration: 2000,
          isClosable: true
        })

        addActivityToCategory(response.data as Activity, response.data.categoryId)
        handleClose()
      } catch (error) {
        if (axios.isAxiosError(error)) {
          if (error.response) {
            log.error('Error creating activity:', error.response.data)
            toast({
              title: `${t('retry_modal_title')} ${error.response.data.message} (${error.response.data.statusCode})`,
              status: 'error',
              duration: 2000,
              isClosable: true
            })
          } else {
            log.error('No response from the server:', error.message)
          }
        }
      }
    }
    setConfirmButtonDisabled(false)
  }
  const toggleCollapseLang = (langCode: string): void => {
    setCollapsedLangs((prevState) => ({
      ...prevState,
      [langCode]: !prevState[langCode]
    }))
  }

  const handleActivityNameChangeCallback = useCallback(
    (langCode: string, value: string) => {
      handleActivityNameChange(langCode, value, showTranslateMenu, activityNames, setActivityNames)
    },
    [activityNames, showTranslateMenu]
  )

  useEffect(() => {
    if (activityNames && activityNames.it !== '' && categoyRef && possibleRoles) {
      if (possibleRoles.length > 0 && possibleRoles.length < 2) {
        setConfirmButtonDisabled(true)
      } else {
        setConfirmButtonDisabled(false)
        setCreateActivityRequest({
          name: activityNames as unknown as LocalizedName,
          categoryId: categoyRef,
          requiresExperienceLevel: requiredExperience,
          possibleRoles: possibleRoles,
          requiresRole: possibleRoles.length > 0
        })
      }
    } else {
      setConfirmButtonDisabled(true)
    }
  }, [categoyRef, activityNames, requiredExperience, possibleRoles])

  useEffect(() => {
    setOpen(isOpen)
  }, [isOpen])

  useEffect(() => {
    eva.replace()
  }, [open, possibleRoles, editingRoleIndex, showNewRoleEdit, showTranslateMenu])

  const handleClose = (): void => {
    onClose()
    setActivityNames({})
    setCategoyRef('')
    setRequiredExperience(false)
    setPossibleRoles([])
    setCreateActivityRequest(undefined)
    setShowTranslateMenu(false)
    setRoleNames({})
    setCurrentRoleValue({})
    setFocusedInput(null)
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} isCentered={true}>
        <ModalOverlay />

        <ModalContent marginBottom={'0px'} borderRadius={'26px 26px 0px 0px'}>
          {showTranslateMenu ? (
            <Flex alignItems='left' direction={'column'}>
              <ModalHeader>
                <Flex align='center' justify='space-between' width='100%'>
                  <Text color={'black'} fontWeight='700' fontSize='24px' whiteSpace='nowrap'>
                    {t('translate_every_lang')}
                  </Text>
                  <Button isDisabled={confirmTranslateButtonDisabled} onClick={(): void => setShowTranslateMenu(false)}>
                    <Flex key={'translate'} align={'center'}>
                      <i data-eva={'globe-outline'} data-eva-fill={'#7C8A97'}></i>
                      <Text color={'greyCold500'} fontWeight='600' fontSize='12px' lineHeight='16.8px' whiteSpace='nowrap'>
                        {t('translate')}
                      </Text>
                    </Flex>
                  </Button>
                </Flex>
              </ModalHeader>
              <ModalBody overflowY={'auto'} maxH={'300px'}>
                {languages.map((lang) => (
                  <Flex
                    key={lang.code}
                    alignItems='left'
                    direction={'column'}
                    bg={'rgba(143, 155, 179, 0.16)'}
                    borderRadius={'12px'}
                    padding={'16px'}
                    gap={'14px'}
                    marginBottom={'20px'}>
                    <Flex justify='space-between'>
                      <Text color={'black'} fontWeight='600' fontSize='22.4px' whiteSpace='nowrap'>
                        {t(lang.name)}
                      </Text>
                      {collapsedLangs[lang.code] ? (
                        <Button onClick={(): void => toggleCollapseLang(lang.code)}>
                          <i data-eva='minus-circle-outline' data-eva-fill={'#DD8B25'}></i>
                        </Button>
                      ) : (
                        <Button onClick={(): void => toggleCollapseLang(lang.code)}>
                          <i data-eva='plus-circle-outline' data-eva-fill={'#DD8B25'}></i>
                        </Button>
                      )}
                    </Flex>
                    {collapsedLangs[lang.code] && (
                      <div>
                        <FormControl id={`${lang.code}-name`}>
                          <FormLabel variant={'primary'}>{t('name_activity')}</FormLabel>
                          <Input
                            bg='white'
                            value={activityNames[lang.code]}
                            onChange={(e): void => handleActivityNameChangeCallback(lang.code, e.target.value)}
                          />
                        </FormControl>
                        {possibleRoles.length > 0 ? (
                          <FormLabel variant={'primary'} marginTop={'5px'}>
                            {t('roles')}
                          </FormLabel>
                        ) : (
                          <></>
                        )}
                        {possibleRoles.map((possibleRole, index) => (
                          <React.Fragment key={index}>
                            <FormControl id='role' mt={'5px'} mb={'5px'}>
                              <Input
                                bg='white'
                                focusBorderColor={confirmTranslateButtonDisabled ? 'redInput' : 'blueInput'}
                                value={possibleRole.name[lang.code]}
                                disabled={confirmTranslateButtonDisabled && focusedInput !== null && focusedInput !== `${index}-${lang.code}`}
                                onFocus={(): void => setFocusedInput(`${index}-${lang.code}`)}
                                onChange={(e): void =>
                                  updatePossibleRoleName(
                                    index,
                                    lang.code,
                                    e.target.value,
                                    setPossibleRoles,
                                    setConfirmTranslateButtonDisabled,
                                    possibleRoles,
                                    toast,
                                    t
                                  )
                                }
                              />
                            </FormControl>
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                  </Flex>
                ))}
              </ModalBody>
              <ModalFooter justifyContent='center'>
                <Button variant='outline' width={'50%'} height='50px' color={'primary.400'} borderColor={'primary.400'} onClick={handleClose} margin={'5px'}>
                  {t('cancel')}
                </Button>

                <Button
                  variant={'primary'}
                  width={'50%'}
                  height='50px'
                  isDisabled={confirmTranslateButtonDisabled}
                  onClick={(): void => setShowTranslateMenu(false)}
                  margin={'5px'}>
                  {t('confirm')}
                </Button>
              </ModalFooter>
            </Flex>
          ) : (
            <Flex alignItems='left' direction={'column'}>
              <ModalHeader>
                <Flex align='center' justify='space-between' width='100%'>
                  <Text color={'black'} fontWeight='700' fontSize='24px' whiteSpace='nowrap'>
                    {t('create_activity')}
                  </Text>
                  <Button onClick={(): void => setShowTranslateMenu(true)}>
                    <Flex key={'translate'} align={'center'}>
                      <i data-eva={'globe-outline'} data-eva-fill={'#7C8A97'}></i>
                      <Text color={'greyCold500'} fontWeight='600' fontSize='12px' lineHeight='16.8px' whiteSpace='nowrap'>
                        Traduci
                      </Text>
                    </Flex>
                  </Button>
                </Flex>
              </ModalHeader>

              <ModalBody overflowY={'auto'} maxH={'300px'}>
                <FormControl id='name'>
                  <FormLabel variant={'primary'}>{t('first_name')}</FormLabel>
                  <Input bg='white' value={activityNames.it} onChange={(e): void => handleActivityNameChangeCallback('it', e.target.value)} />
                </FormControl>

                <FormControl id='category'>
                  <FormLabel variant={'primary'} marginTop={'10px'}>
                    {t('category_ref')}
                  </FormLabel>
                  <Select variant={'primaryBlack'} placeholder={t('placeholder_category_ref')} onChange={handleCategoryRefChange} value={categoyRef}>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.description.it}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl id='roles'>
                  <FormLabel variant={'primary'} marginTop={'10px'}>
                    {t('roles')}
                  </FormLabel>
                  <Flex flexWrap={'wrap'} gap={'10px'}>
                    <Button
                      minW={'170px'}
                      borderRadius='600px'
                      padding={'8px, 10px, 8px, 10px'}
                      gap='2px'
                      bg='black'
                      color={'white'}
                      border={'5px solid primary.400'}
                      fontWeight='600'
                      fontSize='14px'
                      lineHeight='19.6px'
                      onClick={(): void => setShowNewRoleEdit(true)}>
                      {t('add_role')}
                      <i data-eva={'plus-circle-outline'} data-eva-fill={'#FFFFFF'}></i>
                    </Button>
                    {showNewRoleEdit ? (
                      <Flex
                        borderRadius='600px'
                        gap='2px'
                        bg='white'
                        border={'5px solid primary.400'}
                        boxSize={'border-box'}
                        boxShadow={' 0px 2px 10px rgba(0, 0, 0, 0.11)'}
                        fontWeight='600'
                        fontSize='14px'
                        lineHeight='19.6px'
                        justifyContent={'center'}
                        alignItems={'center'}
                        maxW={'200px'}>
                        <Input
                          placeholder={t('role')}
                          value={roleNames.it}
                          onChange={(e): void => handleRoleNameChangeCallback('it', e.target.value)}
                          marginLeft={'10px'}
                          height={'90%'}
                          textOverflow={'ellipsis'}></Input>
                        <Button
                          key={'close'}
                          minW={'fit-content'}
                          onClick={(): void => {
                            setRoleNames({})
                            setShowNewRoleEdit(false)
                          }}
                          paddingInlineStart={'1px'}
                          paddingInlineEnd={'1px'}>
                          <i data-eva={'close-circle-outline'}></i>
                        </Button>
                        <Button
                          key={'check'}
                          minW={'fit-content'}
                          isDisabled={!roleNames.it}
                          onClick={(): void =>
                            handlePossibleRolesChange(possibleRoles, roleNames, setPossibleRoles, setRoleNames, setShowNewRoleEdit, toast, t)
                          }
                          paddingInlineStart={'1px'}
                          paddingInlineEnd={'5px'}>
                          <i data-eva={'checkmark-circle-2'}></i>
                        </Button>
                      </Flex>
                    ) : (
                      <></>
                    )}

                    {possibleRoles.map((possibleRole, index) => (
                      <React.Fragment key={index}>
                        {editingRoleIndex === index ? (
                          <Flex
                            borderRadius='600px'
                            gap='2px'
                            bg='white'
                            border={'5px solid primary.400'}
                            fontWeight='600'
                            fontSize='14px'
                            lineHeight='19.6px'
                            boxSize={'border-box'}
                            boxShadow={' 0px 2px 10px rgba(0, 0, 0, 0.11)'}
                            justifyContent={'center'}
                            alignItems={'center'}
                            maxW={'200px'}>
                            <Input
                              placeholder='Enter role'
                              value={currentRoleValue.it}
                              onChange={(e): void => handleCurrentRoleChangeCallback('it', e.target.value)}
                              textOverflow={'ellipsis'}
                              height={'90%'}
                              marginLeft={'10px'}
                            />
                            <Button minW={'fit-content'} onClick={handleCancelClick} paddingInlineStart={'1px'} paddingInlineEnd={'1px'}>
                              <i data-eva={'close-circle-outline'}></i>
                            </Button>
                            <Button
                              minW={'fit-content'}
                              isDisabled={!currentRoleValue.it}
                              onClick={handleSaveActivityClick}
                              paddingInlineStart={'1px'}
                              paddingInlineEnd={'5px'}>
                              <i data-eva={'checkmark-circle-2'}></i>
                            </Button>
                          </Flex>
                        ) : (
                          <Flex
                            border={'5px solid primary.400'}
                            boxSize={'border-box'}
                            boxShadow={' 0px 2px 10px rgba(0, 0, 0, 0.11)'}
                            padding={'8px 10px'}
                            bg={'white'}
                            borderRadius='600px'
                            gap='2px'
                            alignItems={'center'}
                            height={'40px'}>
                            <Text
                              key={possibleRole.name.it}
                              color={'black'}
                              fontWeight='600'
                              fontSize='14px'
                              lineHeight='19.6px'
                              whiteSpace='nowrap'
                              overflow='hidden'
                              textOverflow='ellipsis'
                              maxWidth='200px'>
                              {possibleRole.name.it}
                            </Text>
                            <Button
                              minW={'fit-content'}
                              key={'editRole'}
                              onClick={(): void => handleEditClick(index, possibleRole.name)}
                              paddingInlineStart={'1px'}
                              paddingInlineEnd={'5px'}>
                              <i data-eva={'edit-2-outline'}></i>
                            </Button>
                          </Flex>
                        )}
                      </React.Fragment>
                    ))}
                  </Flex>
                  {possibleRoles.length < 2 && possibleRoles.length > 0 && (
                    <Flex alignItems='center' color='red.500' mt={2}>
                      <i data-eva={'alert-triangle'} data-eva-fill={'#FF3D71'}></i>
                      <Text marginLeft={'10px'} fontWeight='400' fontSize='12px' lineHeight='16px'>
                        {t('two_roles_required')}
                      </Text>
                    </Flex>
                  )}
                </FormControl>

                <Checkbox mt={'10px'} isChecked={requiredExperience} onChange={handleRequiredExperience}>
                  {t('required_experience')}
                </Checkbox>
              </ModalBody>

              <ModalFooter justifyContent='center'>
                <Button variant='outline' width={'50%'} height='50px' color={'primary.400'} borderColor={'primary.400'} onClick={handleClose} margin={'5px'}>
                  {t('cancel')}
                </Button>
                <Button variant={'primary'} width={'50%'} height='50px' isDisabled={confirmButtonDisabled} onClick={postNewActivity} margin={'5px'}>
                  {t('create')}
                </Button>
              </ModalFooter>
            </Flex>
          )}
        </ModalContent>
      </Modal>
    </>
  )
}

export { CreateActivityModal }
